import { Typology, Status } from '@/components';
import { ColumnDef } from '@/components/Table/types';
import { RequestKpi } from '@/modules/Requests/domain';
import { KpiFilterType, KpiOrderByType } from '@/shared';
import { t } from 'i18next';
import { useMemo } from 'react';
import { KpiDetailsModal } from '../KpiDetailsModal';
import { convertTimeSecondsToString } from '@/utils';

const allowedFilterByTypes = [
  KpiFilterType.Typology,
  KpiFilterType.Status,
  KpiFilterType.Reference,
];

const allowedOrderByTypes = [
  KpiOrderByType.CreationDateDesc,
  KpiOrderByType.CreationDateAsc,
  KpiOrderByType.Typology,
  KpiOrderByType.Status,
  KpiOrderByType.EstimatedHours,
  KpiOrderByType.UsedHours,
];

interface HoursKpiDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const HoursKpiDetailsModal = ({
  isOpen,
  onClose,
}: HoursKpiDetailsModalProps) => {
  const columns = useMemo<ColumnDef<RequestKpi>[]>(
    () => [
      {
        id: 'reference',
        name: t('kpi_reference_column'),
        accessor: 'reference',
      },
      {
        id: 'title',
        name: t('kpi_title_column'),
        accessor: 'title',
        alignment: 'left',
      },
      {
        id: 'typology',
        name: t('kpi_typology_column'),
        accessor: 'typology',
        renderRow: (row) => <Typology type={row.typology} />,
      },
      {
        id: 'createdAt',
        name: t('kpi_createdAt_column'),
        accessor: 'createdAt',
        renderRow: (row) =>
          row.createdAt.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
          }),
      },
      {
        id: 'estimatedHours',
        name: t('kpi_estimatedHours_column'),
        accessor: 'estimatedSeconds',
        renderRow: (row) =>
          convertTimeSecondsToString(row.estimatedSeconds ?? 0),
      },
      {
        id: 'usedHours',
        name: t('kpi_usedHours_column'),
        accessor: 'usedSeconds',
        renderRow: (row) => convertTimeSecondsToString(row.usedSeconds ?? 0),
      },
      {
        id: 'status',
        name: t('kpi_status_column'),
        accessor: 'status',
        renderRow: (row) => <Status status={row.status} />,
      },
    ],
    [],
  );

  return (
    <KpiDetailsModal
      isOpen={isOpen}
      onClose={onClose}
      title={t('requests_hours_kpi_details_title')}
      columns={columns}
      filterByValues={allowedFilterByTypes}
      orderByValues={allowedOrderByTypes}
    />
  );
};
